import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { LEASE_STATUSES, cycleSuffix, type LeaseStatus, type RentCycle, termSuffix, toTerm } from "@rentorng/core";
import { api } from "@/lib/api";
import { ExportButton } from "@/components/export-button";
import { Pagination, SearchBox } from "@/components/list-toolbar";
import { useListParams, type PageMeta } from "@/lib/list-params";
import { titleCase, useFormat } from "@/lib/format";
import { useSession } from "@/lib/session";
import { ExpiryBadge, LeaseStatusBadge } from "@/components/status";
import { Button, Card, EmptyState, ErrorState, Input, LinkButton, PageHeader, Spinner, Table, Tabs } from "@/components/ui";

interface Row {
  id: string;
  reference: string;
  status: string;
  startDate: string;
  endDate: string | null;
  rentAmount: number;
  rentCycle: string;
  rentCycleEvery?: number;
  tenantId: string;
  tenantName: string;
  unitId: string;
  unitLabel: string;
  propertyId: string;
  propertyName: string;
  balance: number;
}

const EXPIRY_TABS = [
  { key: "expired", label: "Expired" },
  { key: "d0_30", label: "≤30 days" },
  { key: "d31_60", label: "31–60 days" },
  { key: "d61_90", label: "61–90 days" },
];

export function LeasesPage() {
  const [params, setParams] = useSearchParams();
  const status = params.get("status") ?? "";
  const expiring = params.get("expiring") ?? "";
  const q = params.get("q") ?? "";
  const { vocab, can } = useSession();
  const fmt = useFormat();

  const list = useListParams();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["leases", status, expiring, list.q, list.page],
    queryFn: () =>
      api.get<{ leases: Row[]; today: string; page: PageMeta }>("/leases", {
        status,
        expiring,
        q: list.q,
        page: list.page,
      }),
    placeholderData: (previous) => previous,
  });

  const rows = data?.leases ?? [];
  const today = data?.today ?? "";

  const tabs = [
    { to: "/app/tenancies", label: "All" },
    ...LEASE_STATUSES.filter((s) => s.live).map((s) => ({ to: `/app/tenancies?status=${s.value}`, label: s.label })),
    ...EXPIRY_TABS.map((t) => ({ to: `/app/tenancies?expiring=${t.key}`, label: t.label })),
  ];
  const current = status ? `/app/tenancies?status=${status}` : expiring ? `/app/tenancies?expiring=${expiring}` : "/app/tenancies";

  return (
    <div>
      <PageHeader
        title={titleCase(vocab.leases)}
        subtitle={`${rows.length} ${rows.length === 1 ? vocab.lease : vocab.leases}`}
        description={"Every agreement, live and finished. Open one to see its schedule, renew it with a rent review, or end it early — expiring ones are listed first."}
        actions={
          <>
            <ExportButton kind="tenancies" />
            {can("lease.edit") ? <LinkButton to="/app/tenancies/new" need="lease.edit">New {vocab.lease}</LinkButton> : null}
          </>
        }
      />

      <Tabs tabs={tabs} current={current} />

      <Card padded={false}>
        <div className="border-b border-ink-200 p-3">
          <SearchBox params={list} placeholder={`Search ${vocab.tenant} or reference…`} />
        </div>

        {isLoading ? (
          <Spinner />
        ) : error ? (
          <ErrorState error={error} retry={() => refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={`No ${vocab.leases} here.`}
            hint={`A ${vocab.lease} links a ${vocab.tenant} to a ${vocab.unit} and is what generates every invoice.`}
            action={can("lease.edit") ? <LinkButton to="/app/tenancies/new" need="lease.edit">New {vocab.lease}</LinkButton> : undefined}
          />
        ) : (
          <Table
            head={
              <tr>
                <th>Reference</th>
                <th>{titleCase(vocab.tenant)}</th>
                <th>{titleCase(vocab.unit)}</th>
                <th>Ends</th>
                <th>Status</th>
                <th className="num">{titleCase(vocab.rent)}</th>
                <th className="num">Owing</th>
              </tr>
            }
          >
            {rows.map((row) => (
              <tr key={row.id}>
                <td><Link to={`/app/tenancies/${row.id}`} className="link">{row.reference}</Link></td>
                <td><Link to={`/app/clients/${row.tenantId}`} className="text-ink-700 hover:underline">{row.tenantName}</Link></td>
                <td>
                  {row.unitLabel}
                  <span className="block text-xs text-ink-400">{row.propertyName}</span>
                </td>
                <td className="text-ink-500">
                  {fmt.date(row.endDate)}
                  <span className="ml-2"><ExpiryBadge endDate={row.endDate} asOf={today} /></span>
                </td>
                <td>
                  <LeaseStatusBadge lease={{ status: row.status as LeaseStatus, startDate: row.startDate, endDate: row.endDate }} asOf={today} />
                </td>
                <td className="num tabular">
                  {fmt.money(row.rentAmount)}
                  <span className="block text-xs text-ink-400">{termSuffix(toTerm(row.rentCycle as RentCycle, row.rentCycleEvery))}</span>
                </td>
                <td className="num tabular">
                  {Number(row.balance) > 0 ? (
                    <span className="font-medium text-danger-700">{fmt.money(Number(row.balance))}</span>
                  ) : (
                    <span className="text-ink-400">—</span>
                  )}
                </td>
              </tr>
            ))}
          </Table>
        )}

        <Pagination meta={data?.page} params={list} noun={vocab.leases} />
      </Card>
    </div>
  );
}
